/**
 * WebAuthn/FIDO2 implementation for hardware-based authentication
 * Enterprise-grade with server-side challenge management
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Convert base64url to ArrayBuffer
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLen);
  const binary = atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return buffer;
}

// Convert ArrayBuffer to base64url
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Check if WebAuthn is supported
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
  );
}

// Check if platform authenticator is available (Touch ID, Face ID, Windows Hello, etc.)
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  try {
    if (!isWebAuthnSupported()) return false;
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// Check if conditional UI is available (autofill)
export async function isConditionalUIAvailable(): Promise<boolean> {
  try {
    if (!isWebAuthnSupported()) return false;
    // Check if the method exists before calling it
    if (typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
      return false;
    }
    return await PublicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

/**
 * Fetch a server-generated challenge for WebAuthn operations
 * This is the enterprise-grade approach that prevents replay attacks
 */
export async function fetchServerChallenge(
  type: 'registration' | 'authentication',
  userEmail?: string
): Promise<{ challenge: string; challengeId: string; expiresAt: string }> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/webauthn-challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      type,
      userEmail
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch challenge');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Challenge generation failed');
  }

  return {
    challenge: data.challenge,
    challengeId: data.challengeId,
    expiresAt: data.expiresAt
  };
}

export interface WebAuthnRegistrationOptions {
  user: {
    id: string;
    email: string;
    name: string;
  };
  challenge?: string;
  authenticatorType?: 'platform' | 'cross-platform' | 'both';
  userVerification?: 'required' | 'preferred' | 'discouraged';
}

export interface WebAuthnCredential {
  credentialId: string;
  publicKey: string;
  counter: number;
  transports?: string[];
  authenticatorAttachment?: string;
  credentialName?: string;
  algorithm?: number;
}

/**
 * Start WebAuthn registration process
 * Now uses server-generated challenges by default
 */
export async function startRegistration(options: WebAuthnRegistrationOptions): Promise<{
  credential: PublicKeyCredential | null;
  credentialData: WebAuthnCredential | null;
}> {
  try {
    // Fetch server-generated challenge if not provided
    let challenge = options.challenge;
    if (!challenge) {
      const serverChallenge = await fetchServerChallenge('registration', options.user.email);
      challenge = serverChallenge.challenge;
    }

    // Create public key credential options
    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge: base64urlToBuffer(challenge),
      rp: {
        name: 'Paslawski Capital Management',
        id: window.location.hostname,
      },
      user: {
        id: new TextEncoder().encode(options.user.id),
        name: options.user.email,
        displayName: options.user.name || options.user.email,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },  // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment:
          options.authenticatorType === 'both' ? undefined :
          options.authenticatorType === 'platform' ? 'platform' : 'cross-platform',
        userVerification: options.userVerification || 'preferred',
        requireResidentKey: false,
        residentKey: 'preferred',
      },
      timeout: 60000,
      attestation: 'direct',
    };

    // Create credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return { credential: null, credentialData: null };
    }

    // Get authenticator data
    const response = credential.response as AuthenticatorAttestationResponse;
    const credentialId = bufferToBase64url(credential.rawId);
    const publicKey = bufferToBase64url(response.getPublicKey()!);

    // Get transports if available
    const transports = response.getTransports ? response.getTransports() : [];

    // Get authenticator attachment
    const authenticatorAttachment = (credential as any).authenticatorAttachment ||
      (options.authenticatorType === 'platform' ? 'platform' : 'cross-platform');

    // Determine algorithm from attestation object (simplified - defaults to ES256)
    const algorithm = -7; // ES256 is most common for passkeys

    const credentialData: WebAuthnCredential = {
      credentialId,
      publicKey,
      counter: 0,
      transports,
      authenticatorAttachment,
      credentialName: getCredentialName(authenticatorAttachment),
      algorithm,
    };

    return { credential, credentialData };
  } catch (error) {
    console.error('WebAuthn registration error:', error);
    throw error;
  }
}

/**
 * Start WebAuthn authentication process
 * Now uses server-generated challenges by default
 */
export async function startAuthentication(options: {
  challenge?: string;
  credentialIds?: string[];
  userVerification?: 'required' | 'preferred' | 'discouraged';
  conditional?: boolean;
}): Promise<{
  credential: PublicKeyCredential | null;
  credentialId: string | null;
  signature: string | null;
  authenticatorData: string | null;
  clientDataJSON: string | null;
}> {
  try {
    // Fetch server-generated challenge if not provided
    let challenge = options.challenge;
    if (!challenge) {
      const serverChallenge = await fetchServerChallenge('authentication');
      challenge = serverChallenge.challenge;
    }

    // Build allowed credentials
    const allowCredentials = options.credentialIds?.map(id => ({
      id: base64urlToBuffer(id),
      type: 'public-key' as const,
      transports: ['internal', 'hybrid', 'usb', 'nfc', 'ble'] as AuthenticatorTransport[],
    }));

    // Create public key credential options
    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64urlToBuffer(challenge),
      allowCredentials: allowCredentials?.length ? allowCredentials : undefined,
      userVerification: options.userVerification || 'preferred',
      timeout: 60000,
      rpId: window.location.hostname,
    };

    // Get credential
    const credential = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
      mediation: options.conditional ? 'conditional' : 'optional',
    }) as PublicKeyCredential;

    if (!credential) {
      return {
        credential: null,
        credentialId: null,
        signature: null,
        authenticatorData: null,
        clientDataJSON: null
      };
    }

    // Get assertion response
    const response = credential.response as AuthenticatorAssertionResponse;
    const credentialId = bufferToBase64url(credential.rawId);
    const signature = bufferToBase64url(response.signature);
    const authenticatorData = bufferToBase64url(response.authenticatorData);
    const clientDataJSON = bufferToBase64url(response.clientDataJSON);

    return {
      credential,
      credentialId,
      signature,
      authenticatorData,
      clientDataJSON,
    };
  } catch (error) {
    console.error('WebAuthn authentication error:', error);
    throw error;
  }
}

/**
 * Get a friendly name for the credential based on authenticator type
 */
function getCredentialName(authenticatorAttachment?: string): string {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  if (authenticatorAttachment === 'platform') {
    // Platform authenticator (built-in)
    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      return 'Face ID / Touch ID';
    } else if (userAgent.includes('Android')) {
      return 'Android Biometrics';
    } else if (platform.includes('Win')) {
      return 'Windows Hello';
    } else if (platform.includes('Mac')) {
      return 'Touch ID';
    }
    return 'Platform Authenticator';
  } else {
    // Cross-platform authenticator (security key)
    return 'Security Key';
  }
}

/**
 * Format credential name with additional context
 */
export function formatCredentialName(
  credential: WebAuthnCredential,
  userAgent?: string
): string {
  let name = credential.credentialName || 'Security Key';

  // Add browser info if available
  if (userAgent) {
    if (userAgent.includes('Chrome')) name += ' (Chrome)';
    else if (userAgent.includes('Safari')) name += ' (Safari)';
    else if (userAgent.includes('Firefox')) name += ' (Firefox)';
    else if (userAgent.includes('Edge')) name += ' (Edge)';
  }

  return name;
}

/**
 * Check if a credential is likely a passkey (platform authenticator with resident key)
 */
export function isPasskey(credential: WebAuthnCredential): boolean {
  return credential.authenticatorAttachment === 'platform';
}
